import axios from 'axios';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

/**
 * Update Check Service
 * Handles version checking and update notifications
 * Always checks against novacheck.nl (global version, not tenant-specific)
 */

const UPDATE_CHECK_KEY = 'lastUpdateCheck';
const UPDATE_DISMISSED_KEY = 'updateDismissedDate';
const BANNER_DISMISSED_KEY = 'bannerDismissedDate';

/**
 * Get the last time an update check was performed
 * @returns {Date|null}
 */
const getLastCheckDate = () => {
    const lastCheck = localStorage.getItem(UPDATE_CHECK_KEY);
    return lastCheck ? new Date(lastCheck) : null;
};

/**
 * Set the last update check date to now
 */
const setLastCheckDate = () => {
    localStorage.setItem(UPDATE_CHECK_KEY, new Date().toISOString());
};

/**
 * Check if the update was dismissed today
 * @returns {boolean}
 */
const wasDismissedToday = () => {
    const dismissedDate = localStorage.getItem(UPDATE_DISMISSED_KEY);
    if (!dismissedDate) return false;

    const dismissed = new Date(dismissedDate);
    const now = new Date();

    // Check if it's the same day
    return dismissed.toDateString() === now.toDateString();
};

/**
 * Mark update as dismissed for today
 */
const dismissUpdate = () => {
    localStorage.setItem(UPDATE_DISMISSED_KEY, new Date().toISOString());
};

/**
 * Check if the banner was dismissed today
 * @returns {boolean}
 */
const wasBannerDismissedToday = () => {
    const dismissedDate = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (!dismissedDate) return false;

    const dismissed = new Date(dismissedDate);
    const now = new Date();

    return dismissed.toDateString() === now.toDateString();
};

/**
 * Mark banner as dismissed for today
 */
const dismissBanner = () => {
    localStorage.setItem(BANNER_DISMISSED_KEY, new Date().toISOString());
};

/**
 * Check if it's time to check for updates (24 hours since last check)
 * @returns {boolean}
 */
const shouldCheckForUpdates = () => {
    const lastCheck = getLastCheckDate();
    if (!lastCheck) return true;

    const now = new Date();
    const hoursSinceLastCheck = (now - lastCheck) / (1000 * 60 * 60);

    return hoursSinceLastCheck >= 24;
};

/**
 * Get current app version info from Capacitor
 * @returns {Promise<{version: string, build: number, platform: string}>}
 */
const getCurrentVersion = async () => {
    try {
        // Use Capacitor.getPlatform() for reliable platform detection
        const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'
        console.log('[UpdateService] Platform detected:', platform);

        // Skip getting app info on web (not supported)
        if (platform === 'web') {
            console.log('[UpdateService] Skipping App.getInfo() on web platform');
            return {
                version: 'web',
                build: 0,
                platform: platform
            };
        }

        console.log('[UpdateService] Getting app info...');
        const info = await App.getInfo();
        console.log('[UpdateService] App.getInfo() returned:', info);

        // Extract build number from version string
        // Format is typically "1.0.175" where 175 is the build number
        const buildNumber = parseInt(info.build || info.version.split('.').pop());
        console.log('[UpdateService] Build number:', buildNumber);

        const result = {
            version: info.version,
            build: buildNumber,
            platform: platform
        };

        console.log('[UpdateService] Current version info:', result);
        return result;
    } catch (error) {
        console.error('[UpdateService] Error getting app info:', error);
        console.error('[UpdateService] Error stack:', error.stack);
        throw error;
    }
};

/**
 * Fetch the latest version from the API
 * @param {string} platform - 'ios' or 'android'
 * @returns {Promise<{version: string, buildNumber: number, isRequired: boolean, notes: string|null}>}
 */
const fetchLatestVersion = async (platform) => {
    try {
        // Always use novacheck.nl for version checks (global, not tenant-specific)
        const apiUrl = 'https://novacheck.nl';

        console.log(`[UpdateService] Fetching from: ${apiUrl}/api/version/${platform}`);
        const response = await axios.get(`${apiUrl}/api/version/${platform}`);
        return response.data;
    } catch (error) {
        console.error('[UpdateService] Error fetching latest version:', error);
        throw error;
    }
};

/**
 * Check for app updates
 * @returns {Promise<{updateAvailable: boolean, currentVersion: string, newVersion: string, buildNumber: number, notes: string|null, platform: string}|null>}
 */
const checkForUpdates = async () => {
    try {
        console.log('[UpdateService] Getting current app version...');
        // Get current version info
        const currentInfo = await getCurrentVersion();
        console.log('[UpdateService] Current app info:', currentInfo);

        // Skip check for web platform
        if (currentInfo.platform === 'web') {
            console.log('[UpdateService] Skipping update check for web platform');
            return null;
        }

        console.log('[UpdateService] Fetching latest version from API...');
        // Fetch latest version from API
        const latestVersion = await fetchLatestVersion(currentInfo.platform);
        console.log('[UpdateService] Latest version from API:', latestVersion);

        // Compare build numbers
        const updateAvailable = latestVersion.buildNumber > currentInfo.build;
        console.log('[UpdateService] Comparison: API build', latestVersion.buildNumber, '> current build', currentInfo.build, '=', updateAvailable);

        // Update last check date
        setLastCheckDate();

        const result = {
            updateAvailable,
            currentVersion: currentInfo.version,
            currentBuild: currentInfo.build,
            newVersion: latestVersion.version,
            newBuild: latestVersion.buildNumber,
            notes: latestVersion.notes,
            isRequired: latestVersion.isRequired,
            platform: currentInfo.platform
        };

        console.log('[UpdateService] ✅ Update check result:', result);

        return result;
    } catch (error) {
        console.error('[UpdateService] ❌ Error checking for updates:', error);
        return null;
    }
};

/**
 * Perform daily update check if needed
 * @returns {Promise<Object|null>}
 */
const dailyUpdateCheck = async () => {
    console.log('[UpdateService] Daily update check called');

    if (!shouldCheckForUpdates()) {
        const lastCheck = getLastCheckDate();
        console.log('[UpdateService] Update check not needed yet. Last check:', lastCheck);
        return null;
    }

    console.log('[UpdateService] Performing update check...');
    return await checkForUpdates();
};

export default {
    checkForUpdates,
    dailyUpdateCheck,
    shouldCheckForUpdates,
    getCurrentVersion,
    wasDismissedToday,
    dismissUpdate,
    wasBannerDismissedToday,
    dismissBanner,
    getLastCheckDate
};
